import React, { useEffect, useState, useRef } from "react"
import { captureException, captureMessage } from "@sentry/minimal"
import { css, Theme } from "@emotion/react"
import {
	makeApiErrorMessage,
	ShippingService,
	ShippitAccessorial,
	useShippitApi,
	ShippitItem,
	ShippitResponse,
	useDefaultAccessorials,
	useIsUser,
	useAuth,
	UserId,
	useEcommFreightEstimation,
} from "@ncs/ncs-api"
import { formatCurrency, formatDate } from "@ncs/ts-utils"
import {
	Box,
	CheckboxGroupEcomm,
	Divider,
	ErrorText,
	GridContainer,
	GridItem,
	LoadingSpinner,
	Paragraph,
	PartClearancePriceEcomm,
	PartImage,
	PartPriceEcomm,
	RadioGroupEcomm,
	SkeletonRows,
	TrackingEvent,
	Flag,
	useIsFeatureFlagOn,
} from "@ncs/web-legos"
import { useShopContext } from "~/contexts"
import { QuantityControl, RemoveFromCartModal } from "~/shared-components"
import { Link } from "react-router-dom"
import { useAnalytics } from "~/hooks/useAnalytics"

interface ShippingSelectorEcommType {
	cost: number | null
	estimatedDelivery: string
	label: string
	name: ShippingService
}

interface ChooseShippingMethodsEcommProps {
	setHasShippingMethod: (value: boolean) => void
}

export const ChooseShippingMethodsEcomm: React.FC<ChooseShippingMethodsEcommProps> = ({
	setHasShippingMethod,
}) => {
	const subdomain = typeof window !== "undefined" ? window.location.hostname.split(".")[0] : ""
	const isEcommServiceFreightEnabled =
		useIsFeatureFlagOn(Flag.VITE_FLAG_ECOMM_FREIGHT) &&
		!localStorage["Legacy"] &&
		subdomain !== "estore"

	const isDb = useIsUser(UserId.DrivenBrands)
	const isMr = useIsUser(UserId.MisterCarwash)
	const [shippitResponse, setShippitResponse] = useState<ShippitResponse | null>(null)

	const [shippitError, setShippitError] = useState<string | null>(null)
	const [partBeingRemoved, setPartBeingRemoved] = useState<ShippitItem | null>(null)
	const [{ cart, checkout }, shopDispatch] = useShopContext()

	const alternateAddressId = checkout.alternateAddressId
	const [accessorialDefaults] = useDefaultAccessorials(
		alternateAddressId ? null : checkout.shipToSiteId
	)
	const auth = useAuth()
	const isPunchout = !!auth.punchOutSessionId
	const isPunchoutAndMr = isPunchout && isMr
	const { trackEvent } = useAnalytics()

	const rawShippitApi = useShippitApi()
	const rawEcommFreightEstimation = useEcommFreightEstimation()
	const shippitApiRef = useRef(rawShippitApi)
	const ecommFreightEstimationRef = useRef(rawEcommFreightEstimation)

	// Get the full, raw shipment option objects from Shippit, with prices for all the choices.
	useEffect(() => {
		const callShippit = async (destination: {
			shipToSiteId?: string
			alternateAddressId?: string | null
			alternateAddress?: {
				address1?: string | null
				address2: string
				city: string
				state: string
				postalcode: string
			}
			billTo: {
				customer_number: string | null
				name: string | null
			}
		}) => {
			try {
				shopDispatch({
					type: "set shippit loading status",
					payload: true,
				})

				const ecommDestination = {
					shipToSiteId: destination.shipToSiteId,
					alternateAddressId: destination.alternateAddressId,
					alternateAddress:
						destination.alternateAddress ?
							{
								streetLines: [
									destination.alternateAddress.address1,
									destination.alternateAddress.address2,
								].filter(Boolean) as string[],
								city: destination.alternateAddress.city,
								state: destination.alternateAddress.state,
								zipCode: destination.alternateAddress.postalcode,
							}
						:	undefined,
					billTo: destination.billTo,
				}
				const [ncsapiResponse, eccommResponse] = await Promise.all([
					shippitApiRef.current(cart, destination),
					ecommFreightEstimationRef.current(cart, ecommDestination),
				])

				const response = eccommResponse?.data

				if (response?.accessorials && ncsapiResponse?.accessorials) {
					response.accessorials = ncsapiResponse?.accessorials
				}

				if (
					response?.shipments?.parts?.service &&
					ncsapiResponse?.shipments?.parts?.service
				) {
					response.shipments.parts.service = ncsapiResponse.shipments.parts.service
				}

				if (
					response?.shipments?.chemicals?.service &&
					ncsapiResponse?.shipments?.chemicals?.service
				) {
					response.shipments.chemicals.service =
						ncsapiResponse.shipments.chemicals.service
				}

				if (response && response.shipments) {
					response.shipments = Object.fromEntries(
						Object.entries(response.shipments).filter(([_, s]) => s.service.length > 0)
					)
				}

				if (!response) {
					// Duplicate request, do not update state.
					return
				}

				if (Object.values(response.shipments).some((s) => s.service.length === 0)) {
					captureMessage("Shippit returned no options for one of the shipments!", {
						extra: {
							partIds: cart.map((item) => item.part.id),
						},
					})
					throw new Error(
						"Unfortunately there was an error getting shipping options for one of the parts in your order. Please contact support."
					)
				}

				// Set local state to what we got back. We'll show this in the UI for the
				// user to choose from.
				setShippitResponse(response)

				// Shop context needs to know how we're splitting the order up.
				shopDispatch({
					type: "set order shipments",
					payload: {
						shipments: Object.entries(response.shipments).map(
							([productType, option]) => {
								// Default to the cheapest (slowest) option.
								const defaultService = option.service.sort((a, b) =>
									(a.cost ?? 0) < (b.cost ?? 0) ? -1 : 1
								)[0]

								return {
									productType,
									parts: option.items,
									service: defaultService.name,
									cost: defaultService.cost,
								}
							}
						),
						hasChemical: Object.keys(response.shipments).some(
							(key) => key === "chemicals"
						),
						billTo: ncsapiResponse?.billTo,
					},
				})
			} catch (e) {
				setShippitError(makeApiErrorMessage(e))
				captureMessage("Shippit returned an error!", {
					extra: {
						cart,
						destination,
						e,
					},
				})
				captureException(e)
				setShippitResponse(null)
				shopDispatch({
					type: "set order shipments",
					payload: {
						shipments: null,
						hasChemical: false,
						billTo: null,
					},
				})
			}
		}

		if (checkout.shipToSiteId || checkout.alternateAddress || checkout.alternateAddressId) {
			setShippitError(null)
			void callShippit({
				shipToSiteId: checkout.shipToSiteId ?? undefined,
				alternateAddressId: checkout.alternateAddressId ?? undefined,
				alternateAddress:
					checkout.alternateAddress ?
						{
							address1: checkout.alternateAddress.address1,
							address2: checkout.alternateAddress.address2,
							city: checkout.alternateAddress.city,
							state: checkout.alternateAddress.state,
							postalcode: checkout.alternateAddress.zip,
						}
					:	undefined,
				billTo: {
					customer_number: checkout.billTo?.customerNumber ?? null,
					name: checkout.billTo?.name ?? null,
				},
			})
		}
	}, [
		cart,
		shopDispatch,
		checkout.shipToSiteId,
		checkout.alternateAddress,
		checkout.alternateAddressId,
	])

	const selectShippingMethod = (
		shipmentProductType: string,
		serviceName: ShippingService,
		serviceCost: number | null
	) => {
		shopDispatch({
			type: "select shipping service for shipment",
			payload: {
				shipmentProductType,
				service: serviceName,
				cost: serviceCost,
			},
		})
		setHasShippingMethod(true)

		if (serviceName === ShippingService.FedExGround) {
			trackEvent(TrackingEvent.GROUND)
		} else if (serviceName === ShippingService.PriorityOvernight) {
			trackEvent(TrackingEvent.OVERNIGHT)
		}
	}

	const onUpdateQuantity = (partId: string, newValue: number) => {
		shopDispatch({
			type: "update part cart quantity",
			payload: {
				partId,
				quantity: newValue,
			},
		})
	}

	const handleAccessorialChange = (isChecked: boolean, accessorial: ShippitAccessorial) => {
		shopDispatch({
			type: "toggle accessorial",
			payload: {
				accessorial,
				isChecked,
			},
		})
	}

	// Whenever either the shippitResponse or the customer accessorial defaults change,
	// set the accessorials in shop context to reflect the defaults.
	useEffect(() => {
		if (
			!checkout.shippitLoading &&
			shippitResponse?.accessorials &&
			Object.keys(shippitResponse.shipments).some((key) => key === "chemicals")
		) {
			shopDispatch({
				type: "set accessorials",
				payload: {
					accessorials: shippitResponse?.accessorials ?? [],
					defaults: accessorialDefaults ?? [],
				},
			})
		}
	}, [checkout.shippitLoading, shippitResponse, accessorialDefaults, shopDispatch])

	if (
		!checkout.shipToSiteId &&
		!checkout.alternateAddress &&
		!checkout.alternateAddressId &&
		!isPunchoutAndMr
	) {
		return <Paragraph color="secondary">First select your shipping address</Paragraph>
	}

	if (cart.length === 0) {
		return <Paragraph color="secondary">There are no products in your cart.</Paragraph>
	}

	const formatShippingDescription = (option: ShippingSelectorEcommType) => {
		if (option.name === ShippingService.FedExGround) {
			return `${option.label}: 2–4 Business Days`
		} else if (option.name === ShippingService.PriorityOvernight) {
			return `${option.label}: 1 Business Day`
		}
		return option.label
	}

	const formatShippingCost = (option: ShippingSelectorEcommType) => {
		let cost = ""

		if (option.label !== ShippingService.TBD && !isDb) {
			cost = `${formatCurrency(option.cost ?? 0)}`
		}

		return (
			!isPunchoutAndMr && (
				<Paragraph
					bold
					color="black"
					css={css`
						float: right;
					`}
				>
					{cost}
				</Paragraph>
			)
		)
	}

	return (
		<>
			{!isPunchoutAndMr && shippitResponse === null && checkout.shippitLoading && (
				<LoadingSpinner
					justifyContent="flex-start"
					text="Calculating shipping options..."
				/>
			)}

			{/* Loop through the shipments we got back from Shippit. */}
			{!!shippitResponse &&
				Object.entries(shippitResponse.shipments).map(
					([shipmentType, shippitShipment], i) => {
						// The shippit response has a bunch of options for this shipment, and we'll
						// look at checkout state to see what's currently chosen.
						const cartShipment = checkout.shipments?.find(
							(s) => s.productType === shipmentType
						)

						const chosenOption = shippitShipment.service.find(
							(serviceOption) => serviceOption.name === cartShipment?.service
						)

						const willCallAddress = () =>
							chosenOption?.address?.map((a: string, index: number) => (
								<Paragraph css={willCallAddressText} key={index}>
									{a}
								</Paragraph>
							))

						return (
							<React.Fragment key={shipmentType}>
								{!!chosenOption &&
									chosenOption?.name !== ShippingService.WWL &&
									!isPunchoutAndMr && (
										<Box display="flex" justifyContent="space-between">
											<Paragraph
												bold
												color="black"
												css={estimatedDeliveryStyle}
											>
												Estimated delivery:
											</Paragraph>
											<Paragraph bold color="success">
												{formatDate(chosenOption.estimatedDelivery)}
											</Paragraph>
										</Box>
									)}
								<Paragraph
									bold
									color="black"
									css={chooseShippingMethodOptionStyle}
								>
									Choose shipping option:
								</Paragraph>
								{checkout.shippitLoading ?
									<SkeletonRows />
								:	<>
										<RadioGroupEcomm
											additionalInformation={willCallAddress()}
											htmlName={`shipment-${shipmentType}-options`}
											options={shippitShipment.service}
											valueAccessor="name"
											extraComponent={formatShippingCost}
											labelAccessor={formatShippingDescription}
											value={chosenOption?.name ?? null}
											onChange={(newValue, option) =>
												selectShippingMethod(
													shipmentType,
													option.name,
													option.cost
												)
											}
										/>
									</>
								}
								<div css={accesorialsContainer}>
									{shipmentType === "chemicals" &&
										!checkout.shippitLoading &&
										!isDb &&
										!isMr && (
											<>
												<Paragraph bold color="black">
													Additional chemical shipping services:
												</Paragraph>

												<CheckboxGroupEcomm
													css={css`
														width: 100%;
													`}
													mt={0.75}
													groupName="categories"
													rows={shippitResponse.accessorials ?? []}
													onChange={(option, newState) =>
														handleAccessorialChange(newState, option)
													}
													valueAccessor={(option) =>
														option.lineItemTypeId.toString()
													}
													labelAccessor="lineItemDescription"
													checkedAccessor={(option) =>
														checkout.accessorials.some(
															(existingAccessorial) =>
																existingAccessorial.lineItemTypeId ===
																option.lineItemTypeId
														)
													}
												/>
											</>
										)}
								</div>
								<GridContainer>
									<GridItem sm={12} xs={12}>
										{shippitShipment.items.map((part) => {
											// First we'll check if it's in the actual cart or not. If it's not, don't show
											// anything, because it probably just got deleted and shippit hasn't updated yet.
											const cartPart = cart.find(
												(p) => p.part.id === String(part.id)
											)
											if (!cartPart) {
												return null
											}

											return (
												<div key={part.id} css={itemList}>
													<div css={item}>
														<Link to={`/shop/parts/${part.id}`}>
															<PartImage
																src={part.imageUrl ?? ""}
																alt={part.title ?? ""}
																isPreview
															/>
														</Link>
														<Link
															to={`/shop/parts/${part.id}`}
															css={underlineHoverStyle}
														>
															<Paragraph bold fontSize={1}>
																{part.title}
															</Paragraph>
															<Paragraph css={partNumber}>
																#{part.partNumber}
															</Paragraph>

															{cartPart.part.clearanceItemDiscount ?
																<PartClearancePriceEcomm
																	netPrice={cartPart.part.price}
																	price={
																		cartPart.part
																			.clearanceItemDiscount
																	}
																	quantity={cartPart.quantity}
																/>
															:	<PartPriceEcomm
																	netPrice={part.price}
																	price={cartPart.part.price}
																	quantity={part.quantity}
																/>
															}
														</Link>
													</div>
													<div css={quantityContainer}>
														<QuantityControl
															unitOfMeasure={part.unitOfMeasure}
															mt={0.5}
															value={part.quantity}
															onChange={(newValue) =>
																onUpdateQuantity(
																	String(part.id),
																	newValue
																)
															}
															onChooseZero={() =>
																setPartBeingRemoved(part)
															}
															useUpdateButton
														/>
													</div>
												</div>
											)
										})}
									</GridItem>
								</GridContainer>

								{i !== (checkout.shipments?.length ?? 0) - 1 && <Divider mb={5} />}
							</React.Fragment>
						)
					}
				)}

			{shippitError && <ErrorText>{shippitError}</ErrorText>}

			<RemoveFromCartModal
				isOpen={!!partBeingRemoved}
				onClose={() => setPartBeingRemoved(null)}
				partId={partBeingRemoved ? String(partBeingRemoved.id) : null}
				partTitle={partBeingRemoved?.title ?? null}
			/>
		</>
	)
}

const chooseShippingMethodOptionStyle = css`
	color: #111827;
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 12px;
`

const estimatedDeliveryStyle = css`
	color: #111827;
	font-size: 16px;
	font-weight: 700;
	margin-bottom: 16px;
`

const quantityContainer = (theme: Theme) => css`
	${theme.breakpoints.down("xs")} {
		margin-left: 6.4rem;
		margin-top: 1.125rem;
	}

	div.quantitySelect,
	> div {
		width: 100%;
	}
`
const itemList = (theme: Theme) => css`
	display: flex;
	justify-content: space-between;
	border-bottom: 1px solid #e5e6e6;
	padding: 1.5rem 0;

	${theme.breakpoints.down("xs")} {
		display: block;
	}
`

const item = css`
	display: flex;
`

const underlineHoverStyle = css`
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`

const partNumber = css`
	color: #4b5563;
	font-size: 0.875rem;
	font-weight: 400;
`

const accesorialsContainer = css`
	margin: 1.125rem 0;
`

const willCallAddressText = css`
	font-size: 0.8rem;
	margin-left: 2rem;
`
